import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { Controller } from 'react-hook-form';
import { z } from 'zod';

import { ApiForm, Field, TextInput, useApiForm } from '../../shared/form';
import { Button } from '../../shared/ui/button';
import { Modal } from '../../shared/ui/modal';
import { Combobox } from '../../shared/ui/combobox';
import {
  blankGraph,
  createFlow,
  flowKeys,
  getNodeCatalog,
  listFlows,
  oneStepGraph,
  untitledFlowName,
  type TFlow,
} from '../../entities/flow';
import { listDestinations, searchDestinations } from '../../entities/destination';
import { folderKeys, listFolders, pathText } from '../../entities/folder';
import { periodLabel } from '../../entities/retention';
import { FieldLabel } from '../../shared/ui/field-label';
import { ClockIcon } from '../../shared/ui/icons';
import { Select } from '../../shared/ui/select';
import { listSources, searchSources } from '../../entities/source';
import { OneStepPreview } from './ui/one-step-preview';

type TStep = 'name' | 'one-step';

const oneStepSchema = z.object({
  sourceId: z.string().min(1, 'Choose a source'),
  destinationId: z.string().min(1, 'Choose a destination'),
});
type TOneStepValues = z.infer<typeof oneStepSchema>;

const nameSchema = z.object({
  name: z.string().trim().min(1, 'Give it a name').max(160, 'At most 160 characters'),
});
type TNameValues = z.infer<typeof nameSchema>;

export interface IFlowCreateProps {
  onCreated: (flow: TFlow) => void;
  label?: string;
  variant?: 'primary' | 'secondary' | 'ghost';
  /** Board 17 "New flow from this source": the one-step form opens with this end chosen. */
  preset?: { sourceId?: string; destinationId?: string };
  /** 'scratch' is the name dialog; 'one-step' goes straight to the source → destination form. */
  renderTrigger?: (openAt: (step: 'one-step' | 'scratch') => void) => ReactNode;
  /** The folder the list is showing: a new flow starts there, with that folder's period. */
  folderId?: string | null;
}

const ROOT = 'root';

// "New flow" asks for a name and nothing else: what starts the flow is chosen on its canvas,
// where it can be changed later. The one-step source → destination path stays a link away.
export const FlowCreate = ({
  onCreated,
  label = 'New flow',
  variant = 'primary',
  renderTrigger,
  preset,
  folderId = null,
}: IFlowCreateProps) => {
  const [open, setOpen] = useState(false);
  const [folder, setFolder] = useState<string>(folderId ?? ROOT);
  const folders = useQuery({
    queryKey: folderKeys.list('flows'),
    queryFn: () => listFolders('flows'),
    enabled: open,
  });
  const folderOptions = [
    { value: ROOT, label: 'Not in a folder' },
    ...(folders.data?.data ?? []).map((entry) => ({
      value: entry.id,
      label: pathText(entry.path),
    })),
  ];
  const chosen = folders.data?.data.find((entry) => entry.id === folder);
  const keeps = folder === ROOT ? folders.data?.rootKeeps : chosen?.keeps;
  const filedIn = folder === ROOT ? undefined : folder;
  const [step, setStep] = useState<TStep>('name');
  const [cameFromName, setCameFromName] = useState(false);
  const navigate = useNavigate();
  const client = useQueryClient();

  // Only the names that could collide with the suggestion; a real clash still comes back
  // from the server as name_taken on the field.
  const untitled = useQuery({
    queryKey: ['flows', 'untitled-names'],
    queryFn: () => listFlows({ q: 'Untitled flow', limit: 200 }),
    enabled: open,
  });
  const suggested = untitledFlowName((untitled.data?.data ?? []).map((flow) => flow.name));

  const [sources, destinations, catalog] = useQueries({
    queries: [
      { queryKey: ['sources'], queryFn: listSources, enabled: open && step === 'one-step' },
      {
        queryKey: ['destinations'],
        queryFn: listDestinations,
        enabled: open && step === 'one-step',
      },
      { queryKey: flowKeys.catalog, queryFn: getNodeCatalog, enabled: open && step === 'one-step' },
    ],
  });

  const sourceOptions = (sources.data?.data ?? []).map((source) => ({
    value: source.id,
    label: source.name,
  }));
  const destinationOptions = (destinations.data?.data ?? []).map((destination) => ({
    value: destination.id,
    label: destination.name,
  }));
  const noSources = sources.isSuccess && sourceOptions.length === 0;
  const noDestinations = destinations.isSuccess && destinationOptions.length === 0;

  const close = () => setOpen(false);

  const named = useApiForm<TNameValues>({
    schema: nameSchema,
    defaultValues: { name: '' },
    onSubmit: async ({ name }) => {
      const flow = await createFlow({
        name,
        graph: blankGraph(),
        publish: false,
        ...(filedIn ? { folderId: filedIn } : {}),
      });
      await client.invalidateQueries({ queryKey: ['flows'] });
      close();
      onCreated(flow);
    },
  });

  const oneStep = useApiForm<TOneStepValues>({
    schema: oneStepSchema,
    defaultValues: { sourceId: '', destinationId: '' },
    onSubmit: async (values) => {
      const flow = await createFlow({
        ...values,
        publish: true,
        ...(filedIn ? { folderId: filedIn } : {}),
      });
      await client.invalidateQueries({ queryKey: ['flows'] });
      close();
      onCreated(flow);
    },
  });

  // The suggestion lands once the names are known, unless someone already typed their own.
  const nameDirty = named.form.formState.isDirty;
  useEffect(() => {
    if (!open || step !== 'name' || !untitled.isSuccess || nameDirty) return;
    if (named.form.getValues('name') === suggested) return;
    named.form.reset({ name: suggested });
    requestAnimationFrame(() => named.form.setFocus('name', { shouldSelect: true }));
  }, [open, step, untitled.isSuccess, nameDirty, suggested, named.form]);

  const chosenSourceId = oneStep.form.watch('sourceId');
  const chosenDestinationId = oneStep.form.watch('destinationId');

  const preview = useMemo(() => {
    const source = sources.data?.data.find((entry) => entry.id === chosenSourceId);
    const destination = destinations.data?.data.find((entry) => entry.id === chosenDestinationId);
    if (!source || !destination) return null;
    return oneStepGraph(source.id, source.name, destination.id, destination.name);
  }, [sources.data, destinations.data, chosenSourceId, chosenDestinationId]);

  const openAt = (target: 'one-step' | 'scratch') => {
    setOpen(true);
    setFolder(folderId ?? ROOT);
    setStep(target === 'one-step' ? 'one-step' : 'name');
    setCameFromName(false);
    named.clearRejection();
    named.form.reset({ name: untitled.isSuccess ? suggested : '' });
    oneStep.clearRejection();
    oneStep.form.reset({
      sourceId: preset?.sourceId ?? '',
      destinationId: preset?.destinationId ?? '',
    });
  };
  const show = (next: boolean) => (next ? openAt('scratch') : close());

  const submitting =
    step === 'one-step' ? oneStep.form.formState.isSubmitting : named.form.formState.isSubmitting;
  const toMissingEnd = () => {
    close();
    void navigate({ to: noSources ? '/sources' : '/destinations' });
  };

  return (
    <>
      {renderTrigger ? (
        renderTrigger(openAt)
      ) : (
        <Button size="sm" variant={variant} onClick={() => show(true)}>
          {label}
        </Button>
      )}

      <Modal
        open={open}
        onOpenChange={show}
        width={step === 'one-step' ? 'md' : 'sm'}
        title={step === 'one-step' ? 'Send a source to a destination' : 'New flow'}
        description={
          step === 'one-step'
            ? 'The simplest flow there is. Nothing in between.'
            : 'Name it; you choose what starts it on the canvas.'
        }
        footerNote={step === 'one-step' ? 'live as v1 straight away' : undefined}
        footer={
          step === 'one-step' && cameFromName ? (
            <Button size="sm" variant="ghost" onClick={() => setStep('name')}>
              Back
            </Button>
          ) : undefined
        }
        submit={
          step === 'one-step' && (noSources || noDestinations)
            ? {
                label: noSources ? 'Add a source first' : 'Add a destination first',
                onSubmit: toMissingEnd,
              }
            : step === 'one-step'
              ? {
                  label: 'Create flow',
                  onSubmit: () => void oneStep.submit(),
                  disabled: submitting,
                  waiting: submitting,
                  waitingLabel: 'Creating',
                }
              : {
                  label: 'Create',
                  onSubmit: () => void named.submit(),
                  disabled: submitting,
                  waiting: submitting,
                  waitingLabel: 'Creating',
                }
        }
      >
        {step === 'name' && (
          <ApiForm api={named}>
            <Field<TNameValues> name="name" label="Name">
              <TextInput
                autoFocus
                maxLength={160}
                placeholder="Untitled flow"
                onFocus={(event) => event.currentTarget.select()}
              />
            </Field>
            <div>
              <FieldLabel htmlFor="new-flow-folder">Folder</FieldLabel>
              <Select
                id="new-flow-folder"
                value={folder}
                onChange={setFolder}
                options={folderOptions}
              />
              {keeps && (
                <p className="mt-2 flex items-center gap-2 rounded-control bg-field-muted px-inset py-label text-meta text-primary">
                  <ClockIcon size="sm" className="text-muted" />
                  Keeps {periodLabel(keeps.period)}
                  {keeps.source === 'folder' && keeps.folderName
                    ? `, from ${keeps.folderName}`
                    : ', the organization default'}
                  . Change it on the flow later.
                </p>
              )}
            </div>
            <p className="text-meta text-muted">
              Or{' '}
              <button
                type="button"
                className="text-accent hover:underline"
                onClick={() => {
                  setCameFromName(true);
                  setStep('one-step');
                }}
              >
                just send a source to a destination
              </button>{' '}
              — two picks, live straight away.
            </p>
            {/* The submit button lives in the footer, outside the form; without this one Enter would do nothing. */}
            <button type="submit" className="hidden" aria-hidden />
          </ApiForm>
        )}

        {step === 'one-step' && (
          <ApiForm api={oneStep}>
            <Field<TOneStepValues> name="sourceId" label="From">
              <Controller
                name="sourceId"
                control={oneStep.form.control}
                render={({ field }) => (
                  <Combobox
                    aria-label="From"
                    noun="source"
                    value={field.value}
                    onChange={field.onChange}
                    options={sourceOptions}
                    search={{ key: ['sources'], find: searchSources }}
                    placeholder="Choose a source"
                  />
                )}
              />
            </Field>

            <Field<TOneStepValues> name="destinationId" label="To">
              <Controller
                name="destinationId"
                control={oneStep.form.control}
                render={({ field }) => (
                  <Combobox
                    aria-label="To"
                    noun="destination"
                    value={field.value}
                    onChange={field.onChange}
                    options={destinationOptions}
                    search={{ key: ['destinations'], find: searchDestinations }}
                    placeholder="Choose a destination"
                  />
                )}
              />
            </Field>

            {(noSources || noDestinations) && (
              <p className="-mt-2 text-meta text-muted">
                A flow needs both ends to exist.{' '}
                {noSources && (
                  <Link to="/sources" className="text-accent">
                    Make a source first
                  </Link>
                )}
                {noSources && noDestinations && ' and '}
                {noDestinations && (
                  <Link to="/destinations" className="text-accent">
                    {noSources ? 'a destination' : 'Make a destination first'}
                  </Link>
                )}
              </p>
            )}

            {preview && catalog.data && (
              <div>
                <p className="mb-label text-meta font-medium text-muted">It will look like this</p>
                <OneStepPreview graph={preview} descriptors={catalog.data.nodeTypes} />
              </div>
            )}

            <button type="submit" className="hidden" aria-hidden />
          </ApiForm>
        )}
      </Modal>
    </>
  );
};
